import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";

import {
  CLAMPQC_PHYSICAL_FAILURE_REASONS_ADMIN_QUERY_KEY,
  CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY,
  appendClampQcPhysicalFailReason,
  deactivateClampQcPhysicalFailReason,
  fetchClampQcPhysicalFailureReasonsAdmin,
  type ClampQcPhysicalFailureReasonAdmin,
} from "./clampQcSettingsApi";

const SECTION_HELP =
  "Mechanical fail reasons for the inspection checklist are stored in quality.failure_reason (test id 1). Operators can add new reasons from the job workspace; module administrators retire reasons here with a required note. Retired reasons stay in the database so historical inspections keep their links; operators cannot add the same label again and see the retirement note if they try.";

export function ClampQcPhysicalFailReasonsSettingsSection(props: {
  canEdit: boolean;
  formFieldsDisabled: boolean;
}): JSX.Element {
  const { canEdit, formFieldsDisabled } = props;
  const queryClient = useQueryClient();
  const [newReasonDraft, setNewReasonDraft] = useState("");
  const [addMessage, setAddMessage] = useState<string | null>(null);
  const [retireTarget, setRetireTarget] = useState<ClampQcPhysicalFailureReasonAdmin | null>(null);
  const [retireNoteDraft, setRetireNoteDraft] = useState("");
  const [retireError, setRetireError] = useState<string | null>(null);

  const adminQuery = useQuery({
    queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_ADMIN_QUERY_KEY,
    queryFn: fetchClampQcPhysicalFailureReasonsAdmin,
    staleTime: 15_000,
    enabled: canEdit,
  });

  const addMutation = useMutation({
    mutationFn: (reason: string) => appendClampQcPhysicalFailReason(reason),
    onSuccess: () => {
      setNewReasonDraft("");
      setAddMessage(null);
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_ADMIN_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY });
    },
    onError: (error) => {
      setAddMessage(error instanceof ApiError ? error.message : "Could not add the fail reason.");
    },
  });

  const retireMutation = useMutation({
    mutationFn: (input: { id: number; note: string }) => deactivateClampQcPhysicalFailReason(input.id, input.note),
    onSuccess: () => {
      setRetireTarget(null);
      setRetireNoteDraft("");
      setRetireError(null);
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_ADMIN_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY });
    },
    onError: (error) => {
      setRetireError(error instanceof ApiError ? error.message : "Could not retire this fail reason.");
    },
  });

  const reasons = adminQuery.data?.reasons ?? [];

  return (
    <>
      <HubSettingsSection title="Physical fail reasons" hoverHint={SECTION_HELP}>
        {!canEdit ? (
          <p className="text-sm text-slate-600">
            Module administrator access is required to manage the mechanical fail reason list.
          </p>
        ) : adminQuery.isError ? (
          <p className="text-sm text-rose-600">
            {adminQuery.error instanceof Error ? adminQuery.error.message : "Could not load fail reasons."}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[16rem] flex-1">
                <Label htmlFor="clamp-qc-new-fail-reason">Add fail reason</Label>
                <Input
                  id="clamp-qc-new-fail-reason"
                  value={newReasonDraft}
                  onChange={(event) => setNewReasonDraft(event.target.value)}
                  disabled={formFieldsDisabled || addMutation.isPending}
                  className="mt-1"
                  autoComplete="off"
                />
              </div>
              <Button
                type="button"
                disabled={formFieldsDisabled || addMutation.isPending || newReasonDraft.trim() === ""}
                onClick={() => {
                  setAddMessage(null);
                  void addMutation.mutate(newReasonDraft);
                }}
              >
                Add reason
              </Button>
            </div>
            {addMessage ? <p className="text-sm text-rose-600">{addMessage}</p> : null}
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Reason</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Retirement note</th>
                    <th className="px-3 py-2">Retired at</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-slate-500">
                        {adminQuery.isLoading ? "Loading…" : "No fail reasons found."}
                      </td>
                    </tr>
                  ) : (
                    reasons.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium text-slate-900">{row.reason}</td>
                        <td className="px-3 py-2">{row.active ? "Active" : "Retired"}</td>
                        <td className="px-3 py-2 text-slate-700">{row.deactivationNote ?? "—"}</td>
                        <td className="px-3 py-2 tabular-nums text-slate-600">
                          {row.deactivatedAt != null ? formatTimestamp(row.deactivatedAt) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {row.active ? (
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={formFieldsDisabled}
                              onClick={() => {
                                setRetireTarget(row);
                                setRetireNoteDraft("");
                                setRetireError(null);
                              }}
                            >
                              Retire
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </HubSettingsSection>

      {retireTarget != null
        ? createPortal(
            <div
              {...hubModalOverlayProps(false)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="clamp-qc-retire-reason-title"
            >
              <div className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl">
                <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
                  <h2 id="clamp-qc-retire-reason-title" className="text-lg font-semibold text-slate-900">
                    Retire fail reason
                  </h2>
                </div>
                <div className="space-y-3 px-4 py-4 sm:px-5">
                  <p className="text-sm text-slate-700">
                    Retiring <span className="font-semibold">{retireTarget.reason}</span> removes it from the
                    inspection checklist. Operators who try to add this label again will see your note below.
                  </p>
                  <div>
                    <Label htmlFor="clamp-qc-retire-note">Deactivation note</Label>
                    <Input
                      id="clamp-qc-retire-note"
                      value={retireNoteDraft}
                      onChange={(event) => setRetireNoteDraft(event.target.value)}
                      disabled={retireMutation.isPending}
                      className="mt-1"
                      autoComplete="off"
                    />
                  </div>
                  {retireError ? <p className="text-sm text-rose-600">{retireError}</p> : null}
                </div>
                <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={retireMutation.isPending}
                    onClick={() => {
                      setRetireTarget(null);
                      setRetireNoteDraft("");
                      setRetireError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={retireMutation.isPending || retireNoteDraft.trim() === ""}
                    isLoading={retireMutation.isPending}
                    onClick={() => {
                      setRetireError(null);
                      void retireMutation.mutate({ id: retireTarget.id, note: retireNoteDraft });
                    }}
                  >
                    Retire reason
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
